import React from 'react';
import { CSSTransition } from 'react-transition-group';
import { Link, withRouter } from 'react-router-dom';
import IdleMonitor from 'react-simple-idle-monitor';
import classNames from 'classnames';
import striptags from 'striptags';
import { find } from 'lodash';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { fetchMediaItem } from '../../actions';

import { Play, Pause, Fullscreen, Unfullscreen } from '../parts/svg.jsx';
import PlayBar from '../parts/PlayBar';
import { getVideoLabel, getOrientation, requestFullscreen, exitFullscreen } from '../../helpers.js';
import isTouch from 'is-touch';

import Div100vh from 'react-div-100vh';
import moment from 'moment';

class Video extends React.Component {
  constructor(props){
    super(props);

    this.state = {
      paused: false,
      muted: true,
      currentTime: false,
      duration: false,
    };
    this.video = React.createRef();
  }

  componentWillUnmount(){
    this.props.onUnmount();
    this._detachEvents();
    document.body.classList.remove('black');
    document.body.classList.remove('video-paused');
  }

  shouldComponentUpdate(nextProps, nextState){
    const u = (JSON.stringify(this.props) !== JSON.stringify(nextProps)) || (JSON.stringify(this.state) !== JSON.stringify(nextState));
    return u;
  }

  componentDidMount(){
    document.body.classList.add('black');
    this.props.fetchMediaItem(this.props.match.params.id);

    if(this.props.labelString){
      this._setHeaderLabels();
    }
  }

  componentDidUpdate(prevProps){
    const { video } = this.props;

    if(prevProps.match.params.id !== this.props.match.params.id){
      this.setState({
        paused: false
      });
    }

    if(prevProps.labelString){
      // has already had label
      if(prevProps.labelString !== this.props.labelString){
        // has different labels
        this._setHeaderLabels();
      }else{
        // has same labels, but different video
        if(prevProps.match.params.id !== this.props.match.params.id){
          this._setHeaderLabels();
        }
      }
    }else{
      if(this.props.labelString){
        this._setHeaderLabels();
      }
    }
  }

  isFullscreen = () => {
    return window.outerHeight === window.screen.height && !isTouch;
  }

  _setHeaderLabels = () => {
    this.props.onMount(this.props.label);
  }

  _handleVideoEnded = () => {
    setTimeout(() => {
      this.props.history.push(`${this.props.nextItem.id}`);
    }, 2000);
  }

  _playVideo(){
    this.video.current.play();
    this.setState({paused: false});
    document.body.classList.remove('video-paused');
  }

  _pauseVideo(){
    this.setState({paused: true});
    this.video.current.pause();
    document.body.classList.add('video-paused');
  }

  _handleOrientationChange = e => {
    const orientation = getOrientation(e);

    if(orientation === 90 || orientation === -90){
      this._handleFullScreen();
    }
  }

  _handlePlayPause = () => {
    if(this.video.current.paused){
      this._playVideo();
    }else{
      this._pauseVideo();
    }
  }

  _handleFullScreen = e => {
    if(this.video){
      if(isTouch){
        requestFullscreen(this.video.current);
      }else{
        console.log('is full? ', this.isFullscreen());
        if(this.isFullscreen()){
          exitFullscreen();
        }else{
          requestFullscreen();
        }
      }
    }
  }

  _handleMute = e => {
    this.video.current.muted = !this.video.current.muted;
    this.setState({muted:this.video.current.muted});
  }

  _attachEvents = () => {
    this.video.current.addEventListener('ended', this._handleVideoEnded, false);
    document.addEventListener('keydown', this._handleKeyPress);

    if(isTouch){
      window.addEventListener('orientationchange', this._handleOrientationChange, false);
    }
  }

  _detachEvents = () => {
    this.video.current.removeEventListener('ended', this._handleVideoEnded, false);
    document.removeEventListener('keydown', this._handleKeyPress);

    if(isTouch){
      window.removeEventListener('orientationchange', this._handleOrientationChange, false);
    }
  }

  _handleKeyPress = e => {
    const { director } = this.props;

    switch (e.code) {
      case 'ArrowLeft':
        this.video.current.currentTime -= 5;
        break;
      case 'ArrowRight':
        this.video.current.currentTime += 5;
        break;
      case 'Space':
        this._handlePlayPause();
        break;
      case 'KeyM':
        this._handleMute();
        break;
      case 'Escape':
        if(director.acf.hide_artist){
          this.props.history.push(`/`);
        }else{
          this.props.history.push(`/directors/${director.slug}`, { fromDetailView: true });
        }
        break;
      default:

    }
  }

  _handleOnCanPlay = e => {
    this._playVideo();
    this._attachEvents();

    this.setState({duration: this.video.current.duration});

    setInterval(() => {
      if(this.video.current){
        this.setState({currentTime: this.video.current.currentTime});
      }
    }, 44);
  }

  _handleTimeUpdate = e => {
    this.setState({currentTime: this.video.current.currentTime});
  }

  _handlePositionChange = targetSecond => {
    this.video.current.currentTime = targetSecond;
  }

  render () {
    const { director, video, previousItem, nextItem } = this.props;

    if(!video){
      return false;
    }

    return <div className={classNames('page page--video transition', {
        'paused': this.state.paused
      })} style={{color: director && director.primary_color}}>
        <IdleMonitor
            activeClassName="idle-detector"
            idleClassName="idle-detector--active"
            timeout={2000}
            onIdle={() => document.body.classList.add('video-idle')}
            onActive={() => document.body.classList.remove('video-idle')} />
        <Helmet>
          <meta name='description' content={`${video.title} by ${director.title.rendered} for ${video.client} with AKKURAT Studios. ${striptags(director.content.rendered)}`} />
          <meta name='og:description' content={`${video.title} by ${director.title.rendered} for ${video.client} with AKKURAT Studios. ${striptags(director.content.rendered)}`} />
          <meta property="og:video" content={video.url} />
          <meta property="og:video:height" content={video.height} />
          <meta property="og:video:width" content={video.width} />
          <meta property="og:video:type" content={`${video.type}/${video.subtype}`} />
        </Helmet>
        <Div100vh className="video" onClick={this._handlePlayPause}>
          {video &&
            <React.Fragment>
              <video
                ref={this.video}
                type={`${video.type}/${video.subtype}`}
                src={video.url}
                autoPlay
                playsInline
                muted
                onCanPlay={e => this._handleOnCanPlay(e)}
                onTimeUpdate={e => this._handleTimeUpdate(e)}
                />
              <PlayBar
                currentTime={this.state.currentTime}
                duration={this.state.duration}
                onPositionChange={targetSecond => this._handlePositionChange(targetSecond)}
                />
            </React.Fragment>
          }
        </Div100vh>

        <div className="video__footer">
          {/*!director.acf.hide_artist ?
            <Link to={{
                pathname: `/directors/${director.slug}`,
                state: {
                  fromDetailView: true
                }
            }}>Artist Page</Link>
            : <div />
          */}

          <div className={'video__fullscreen'} onClick={() => this._handleFullScreen()}>
            {this.isFullscreen() ? <Unfullscreen /> : <Fullscreen />}
          </div>
          <div className={classNames('video__mute', {'video__mute--muted': this.state.muted})} onClick={this._handleMute}>
            {this.state.muted ?
              <span>Sound is off</span>
              :
              <span>Sound is on</span>
            }
          </div>
        </div>
        {!director.acf.hide_artist &&
          <React.Fragment>
            <Link className="navigation navigation--prev" to={`/directors/${director.slug}/${previousItem.id}`}>Previous</Link>
            <Link className="navigation navigation--next" to={`/directors/${director.slug}/${nextItem.id}`}>Next</Link>
          </React.Fragment>
        }
      </div>;
  }
}

const mapStateToProps = (state, ownProps) => {
  let nextItem;
  let previousItem;

  if(!state.directors.loaded){
    return {};
  }

  const directors = state.directors.directors;
  const media = state.media.media;

  const director = find(directors, {slug: ownProps.match.params.slug });
  const videoItem = find(director.acf.videos, {video: {id: parseInt(ownProps.match.params.id)}});
  const video = {
    ...videoItem.video,
    title: videoItem.title,
    client: videoItem.client,
  };

  if(director){
    const currentVideoIndex = director.acf.videos.indexOf(videoItem);
    const totalVideoCount = director.acf.videos.length

    if(currentVideoIndex >= 0 && currentVideoIndex < totalVideoCount - 1){
      nextItem = director.acf.videos[currentVideoIndex + 1].video
    }else{
      nextItem = director.acf.videos[0].video;
    }

    if(currentVideoIndex === 0){
      previousItem = director.acf.videos[totalVideoCount - 1].video;
    }else{
      previousItem = director.acf.videos[currentVideoIndex - 1].video
    }
  }

  return {
    video,
    director,
    previousItem,
    nextItem,
    label: getVideoLabel(director, video.title, !videoItem.hide_phrase && video.client),
    labelString: director + video.title + video.client
  }
}


const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    fetchMediaItem: id => {
      dispatch(fetchMediaItem(id));
    },
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Video));
